import { msg } from '@lingui/core/macro';
import styled from '@emotion/styled';
import { Trans, useLingui } from '@lingui/react/macro';
import { Object as FabricObject } from 'fabric/fabric-impl';
import { ChevronLeft, Copy, Download, Image as ImageIcon, RotateCcw, Save } from 'lucide-react';
import { useFabricEditorOptional } from 'modules/fabric-editor';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { mediaKindLabel } from 'modules/manager/domain/media-kind';
import { selectActiveTemplate, selectError } from 'modules/manager/store';
import { useResetTemplate, useSaveTemplate } from 'modules/manager/ui/hooks';
import useDuplicateTemplateModal from 'modules/manager/ui/hooks/modals/use-duplicate-template-modal';
import { useExportTemplates } from 'modules/manager/ui/hooks/use-export-templates';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useIsLocal from 'shared/hooks/use-is-local';
import { useAppSelector } from 'store/hooks';
import { editorAccent } from '../editor-accent';
import RenderPreviewModal from './render-preview-modal';
import { theme } from 'styles/theme/theme';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';
import UDActionButton from 'ud-ui/components/action-button';

const EditorHeader: React.FC = () => {
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const navigate = useNavigate();
  const template = useAppSelector(selectActiveTemplate);
  // Пока шаблона нет, `error` слайса — это ошибка его загрузки: плашка имени не мерцает вечно.
  const loadError = useAppSelector(selectError);
  const editor = useFabricEditorOptional();
  const { save, isDirty, isSaving, canSave, canEdit } = useSaveTemplate(editor);
  const { reset, canReset } = useResetTemplate(editor);
  const isLocal = useIsLocal();
  const [isRenderPreviewOpen, setRenderPreviewOpen] = useState(false);

  const handleBack = useCallback(() => {
    if (template?.templateGroupId) {
      navigate(Pages.MEDIA_MANAGER.builders.group(template.templateGroupId));
      return;
    }
    navigate(Pages.MEDIA_MANAGER.INDEX);
  }, [navigate, template?.templateGroupId]);

  // From the editor the user is already editing, so open the copy straight away
  // instead of bouncing through the list.
  const onDuplicated = useCallback((copy: MediaManagerTemplate) => navigate(Pages.MEDIA_MANAGER.builders.editor(copy.id)), [navigate]);

  const { modal: duplicateModal, open: openDuplicateModal, canDuplicate, isDuplicating } = useDuplicateTemplateModal({ onDuplicated });

  // The file carries the canvas as it is on screen, unsaved edits included, and
  // nothing is written to the template. The canvas is serialized exactly the way
  // saving does it; layout and ranking come from `activeTemplate` — the right
  // toolbar persists them the moment they change.
  const { exportTemplates } = useExportTemplates();
  const handleExport = useCallback(() => {
    if (!template) return;
    const canvas = editor?.canvas.toJSON(['data']) as { version: string; objects?: FabricObject[] } | undefined;
    const snapshot = canvas ? { ...template, objects: canvas.objects ?? [], version: canvas.version } : template;
    // Промис — кнопке «Экспорт»: она заперта до конца выгрузки (IMGM-1561).
    return exportTemplates([snapshot], { complete: true }).catch((error) => {
      console.error('Failed to export media manager template:', error);
      toastApiError(error, msg`Не удалось выгрузить шаблон, попробуйте ещё раз`);
    });
  }, [editor, exportTemplates, template, toastApiError]);

  const canDevRender = isLocal && template != null;
  // A regular user opened a global preset: it is read-only until they duplicate it.
  const isReadOnlyPreset = template != null && !canEdit;

  return (
    <Root>
      <BackButton type="button" onClick={handleBack} aria-label={t`Назад`}>
        <ChevronLeft size={18} />
      </BackButton>
      <Title>
        {template || loadError ? (
          <span className="font-semibold">{template?.name ?? '—'}</span>
        ) : (
          // Строка заголовка — 21px (14px текста); плашка ниже, чтобы шапка не меняла высоту.
          <span className="skeleton-block inline-block" style={{ width: 160, height: 14 }} data-testid="editor-header-name-skeleton" />
        )}
        {template && <KindLabel data-testid="editor-header-kind">{t(mediaKindLabel(template.mediaKind))}</KindLabel>}
        {isReadOnlyPreset ? (
          <ReadOnlyBadge>
            <Trans>Пресет — дублируйте, чтобы редактировать</Trans>
          </ReadOnlyBadge>
        ) : (
          isDirty && (
            <UnsavedBadge>
              <Trans>Несохранённые изменения</Trans>
            </UnsavedBadge>
          )
        )}
      </Title>
      <Spacer />
      {canDevRender && (
        <DevRenderButton
          type="button"
          onClick={() => setRenderPreviewOpen(true)}
          title={t`Только для локальной разработки. Отрисовать шаблон на бэкенде с тестовыми параметрами. Рендерится сохранённая версия шаблона — несохранённые изменения в картинку не попадут.`}
        >
          <ImageIcon size={16} />
          <span>
            <Trans>Рендер</Trans>
          </span>
        </DevRenderButton>
      )}
      {template && (
        <UDActionButton
          as={OutlineButton}
          type="button"
          onClick={handleExport}
          aria-label={t`Экспорт в файл`}
          title={
            isDirty
              ? t`В файл попадут текущие правки — сам шаблон при этом не сохранится`
              : t`Скачать шаблон файлом, чтобы загрузить его в другую организацию или на другой стенд`
          }
        >
          <Download size={16} />
          <span>
            <Trans>Экспорт</Trans>
          </span>
        </UDActionButton>
      )}
      {isReadOnlyPreset ? (
        <SaveButton
          type="button"
          onClick={() => template && openDuplicateModal(template)}
          disabled={!canDuplicate || isDuplicating}
          aria-label={t`Дублировать`}
          title={canDuplicate ? undefined : t`Нет активного турнира для копии`}
        >
          <Copy size={16} />
          <span>{isDuplicating ? t`Копирование…` : t`Дублировать`}</span>
        </SaveButton>
      ) : (
        <>
          <OutlineButton
            type="button"
            onClick={reset}
            disabled={!canReset || isSaving}
            aria-label={t`Сбросить изменения`}
            title={t`Вернуть шаблон к последнему сохранённому состоянию. Сам сброс отменяется через Ctrl+Z.`}
          >
            <RotateCcw size={16} />
            <span>
              <Trans>Сбросить</Trans>
            </span>
          </OutlineButton>
          {/* Своя кнопка шапки с китовым замком: промис `save` держит её до ответа (IMGM-1561). */}
          <UDActionButton as={SaveButton} type="button" onClick={save} disabled={!canSave} aria-label={t`Сохранить`}>
            <Save size={16} />
            <span>{isSaving ? t`Сохранение…` : t`Сохранить`}</span>
          </UDActionButton>
        </>
      )}
      {duplicateModal}
      {isRenderPreviewOpen && template && <RenderPreviewModal template={template} onClose={() => setRenderPreviewOpen(false)} />}
    </Root>
  );
};

export default EditorHeader;

const Root = styled.header`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  height: 56px;
  border-bottom: 1px solid ${theme.colors.DarkBlue100};
  background: ${theme.colors.SurfaceWhite};
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  color: ${theme.colors.DarkBlue400};
  &:hover {
    background: ${theme.colors.SurfaceCultured10};
  }
`;

const Title = styled.div`
  font-size: 14px;
  color: ${theme.colors.DarkBlue600};
  display: flex;
  align-items: center;
`;

const KindLabel = styled.span`
  margin-left: 8px;
  color: ${theme.colors.DarkBlue400};
  font-size: 12px;
  font-weight: 400;
`;

const UnsavedBadge = styled.span`
  margin-left: 12px;
  padding: 2px 8px;
  border-radius: 9999px;
  background: ${theme.colors.DarkYellow100};
  /* warning читается только на тёмном; на этом чипе он даёт 1.88:1, нужен тёмный янтарь. */
  color: ${theme.colors.DarkYellow800};
  font-size: 12px;
  font-weight: 500;
`;

const ReadOnlyBadge = styled.span`
  margin-left: 12px;
  padding: 2px 8px;
  border-radius: 9999px;
  background: ${editorAccent.soft};
  color: ${editorAccent.ink};
  font-size: 12px;
  font-weight: 500;
`;

const Spacer = styled.div`
  flex: 1;
`;

/** Local-only — see dev-render-params.ts. Styled as a secondary action so it never competes with Save. */
const DevRenderButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid ${theme.colors.DarkBlue200};
  color: ${theme.colors.DarkBlue400};
  font-size: 14px;
  font-weight: 500;
  &:hover {
    background: ${theme.colors.BorderGray50};
  }
`;

// Secondary header actions (reset, export). Discarding edits is undoable (Ctrl+Z),
// so reset stays a quiet outline button rather than a red destructive one — and
// neither competes with Save.
const OutlineButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid ${theme.colors.DarkBlue200};
  color: ${theme.colors.DarkBlue400};
  font-size: 14px;
  font-weight: 500;
  &:hover:not(:disabled) {
    background: ${theme.colors.BorderGray50};
  }
  &:disabled {
    color: ${theme.colors.DarkBlue200};
    border-color: ${theme.colors.DarkBlue100};
    cursor: not-allowed;
  }
`;

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  background: ${editorAccent.solid};
  color: ${editorAccent.onSolid};
  font-size: 14px;
  font-weight: 500;
  &:hover:not(:disabled) {
    background: ${editorAccent.solidHover};
  }
  &:disabled {
    background: ${theme.colors.DarkBlue100};
    color: ${theme.colors.DarkBlue300};
    cursor: not-allowed;
  }
`;
